import {
  Card,
  Col,
  Form,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
} from "antd";
import styled from "styled-components";
import "./select-procedure.css";
import { useQuote } from "../QuoteProvider";
import { ProcedureType } from "../../../types/procedure_type";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { CheckOutlined, UnorderedListOutlined } from "@ant-design/icons";
import QuoteSummary from "../QuoteSummary";
import QuoteProceduresList from "../components/QuoteProceduresList/QuoteProceduresList";
import QuoteButton from "../components/QuoteButton/QuoteButton";
import QuoteFiltersForm from "../components/QuoteFiltersForm/QuoteFiltersForm";
import { Procedure } from "../../../types/procedures";
import TextArea from "antd/es/input/TextArea";
import QuoteProceduresListAll from "../components/QuoteProceduresList/QuoteProceduresListAll";
import { ErrorBoundary } from "../../ErrorBoundary";
import ProcedureStoryLoading, {
  ProcedureStoryUnavailable,
} from "../components/ProcedureStory/ProcedureStoryLoading";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../MobileBreakpointContext";
import {
  trackQuoteHelpChooseOpened,
  trackQuoteProcedureCtaClicked,
  trackQuoteProcedureCtaDisabledClicked,
  trackQuoteProcedureTypeRemoved,
  trackQuoteProcedureTypeSelected,
  trackQuoteSimplifiedProcedureCtaClicked,
} from "../../../utils/quoteAnalytics";
import { uploadedImagePath } from "../../../utils/uploadedImage";

// Lazy-loaded so the story surface (its Relay episodes query, the Fraunces
// face and the whole player layer) stays OUT of the funnel's eager main.js —
// only a patient who actually taps ⓘ pays for it. Same pattern as
// BookNowButton → BookingPaymentPanel.
const ProcedureStory = lazy(
  () => import("../components/ProcedureStory/ProcedureStory"),
);

export type Option = {
  value: string;
  label: string;
};

type Step1SelectProcedureV2Props = {
  next: () => void;
};

const Step1SelectProcedureV2 = ({ next }: Step1SelectProcedureV2Props) => {
  // @ts-ignore - useTranslation with namespace parameter
  const { t } = useTranslation("quote");
  // @ts-ignore
  const { t: tProcedures } = useTranslation("procedures");
  const [storyProcedure, setStoryProcedure] = useState<Procedure | null>(null);
  const isMobile = useIsMobile();
  const quoteData = useQuote();
  const [isSimplifiedMode, setIsSimplifiedMode] = useState(false);
  // Disabled-CTA feedback (2026-09-13 usability fix): 73 sessions tapped the
  // disabled Continue button since 2026-08-01 with zero visual response; the
  // 28 with no selection at all abandoned 28/28. Gated on hasSelection below
  // so the hint disappears the moment a selection lands — no timers needed.
  const [showDisabledHint, setShowDisabledHint] = useState(false);
  const proceduresListRef = useRef<HTMLDivElement>(null);
  const [modal, modalContextHolder] = Modal.useModal();

  const [selectedProcedureType, setSelectedProcedureType] = useState("");
  // Only types that actually have a live procedure behind them. Desktop's
  // radio column has always dropped the empty ones; the mobile pill row used to
  // get the UNFILTERED list, so a tap on an empty category left the patient
  // staring at a blank list with nothing to select (2026-09-13 audit). One
  // filtered list now feeds both surfaces.
  const procedureTypeOptions: Option[] = quoteData.procedureTypes
    .filter((procedureType: ProcedureType) =>
      quoteData.procedures.some((procedure: Procedure) =>
        (procedure.procedureTypes || []).some(
          (pt) => pt.id === procedureType.id,
        ),
      ),
    )
    .map((procedureType: ProcedureType) => {
      return {
        value: procedureType.id,
        label: tProcedures(`types.${procedureType.name}`, {
          defaultValue: procedureType.name,
        }),
      };
    });
  const simplifiedProcedureTypes = quoteData.procedureTypes.filter(
    (procedureType: ProcedureType) =>
      procedureType.isInternal !== true &&
      procedureType.useForSimplifiedFlow === true,
  );
  const selectedProcedureTypeIds = quoteData.selectedProcedureTypes.map(
    (procedureType) => procedureType.id,
  );
  const surface = isMobile ? "mobile" : "desktop";

  useEffect(() => {
    // Set "Show All" as the default (especially for mobile)
    if (selectedProcedureType === "" && procedureTypeOptions.length > 0) {
      setSelectedProcedureType("all");
    }
  }, [procedureTypeOptions]);

  // Auto-select "all" on mobile
  useEffect(() => {
    if (isMobile && selectedProcedureType === "") {
      setSelectedProcedureType("all");
    }
  }, [isMobile]);

  useEffect(() => {
    if (quoteData.selectedProcedureTypes.length > 0) {
      setIsSimplifiedMode(true);
    }
  }, [quoteData.selectedProcedureTypes.length]);

  const selectedCount = quoteData.selectedProcedures.length;
  const selectedProcedureIds = quoteData.selectedProcedures.map((p) => p.id);
  const selectedSimplifiedCount = quoteData.selectedProcedureTypes.length;
  const hasSelection = selectedCount > 0 || selectedSimplifiedCount > 0;
  // Real i18next plurals (`_one` / `_other`) — the old flat keys read
  // "Continue with 1 area(s) selected", and because the key EXISTED the
  // hand-written defaultValue plural logic that used to sit here never ran.
  const ctaCopy =
    selectedSimplifiedCount > 0
      ? t("procedures.continueWithAreas", { count: selectedSimplifiedCount })
      : selectedCount > 0
      ? t("procedures.continueWithSelected", { count: selectedCount })
      : isSimplifiedMode
        ? t("procedures.selectAreaToContinue", {
            defaultValue: "Select an area to continue",
          })
        : t("procedures.selectToContinue", {
          defaultValue: "Select a procedure to continue",
        });

  const handleDisabledCtaClick = () => {
    trackQuoteProcedureCtaDisabledClicked();
    setShowDisabledHint(true);
    proceduresListRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Both mode switches DISCARD the other mode's picks. They used to do it
  // silently, so a patient who had selected four procedures and tapped "Help me
  // choose" (or picked areas and then touched a type filter) lost the lot with
  // no warning. Confirm first when there is something to lose; switch straight
  // through when there isn't.
  // Modal.useModal (not the static Modal.confirm) so the dialog inherits the
  // funnel's local ConfigProvider theme — the static form can't read React
  // context and would render in antd's default blue.
  const confirmDiscard = (
    titleKey: string,
    bodyKey: string,
    onConfirm: () => void,
  ) => {
    modal.confirm({
      title: t(titleKey),
      content: t(bodyKey),
      okText: t("procedures.switchConfirmOk"),
      cancelText: t("procedures.switchConfirmCancel"),
      centered: true,
      onOk: onConfirm,
    });
  };

  const openSimplifiedMode = () => {
    const applySwitch = () => {
      quoteData.clearSelectedProcedures();
      setIsSimplifiedMode(true);
      trackQuoteHelpChooseOpened(surface);
    };
    if (selectedCount > 0) {
      confirmDiscard(
        "procedures.switchToAreasTitle",
        "procedures.switchToAreasBody",
        applySwitch,
      );
      return;
    }
    applySwitch();
  };

  // `afterSwitch` runs only once the switch is actually applied, so a cancelled
  // confirm leaves BOTH the mode and the type filter exactly as they were.
  const selectExactMode = (afterSwitch?: () => void) => {
    const applySwitch = () => {
      setIsSimplifiedMode(false);
      quoteData.setSelectedProcedureTypes([]);
      afterSwitch?.();
    };
    if (selectedSimplifiedCount > 0) {
      confirmDiscard(
        "procedures.switchToExactTitle",
        "procedures.switchToExactBody",
        applySwitch,
      );
      return;
    }
    applySwitch();
  };

  const toggleProcedureType = (procedureType: ProcedureType) => {
    const isSelected = selectedProcedureTypeIds.includes(procedureType.id);
    const nextProcedureTypes = isSelected
      ? quoteData.selectedProcedureTypes.filter(
          (selectedType) => selectedType.id !== procedureType.id,
        )
      : [...quoteData.selectedProcedureTypes, procedureType];

    quoteData.setSelectedProcedureTypes(nextProcedureTypes);

    if (isSelected) {
      trackQuoteProcedureTypeRemoved({
        procedureTypeId: procedureType.id,
        selectedCountAfter: nextProcedureTypes.length,
        surface,
      });
      return;
    }

    trackQuoteProcedureTypeSelected({
      procedureTypeId: procedureType.id,
      selectedCountAfter: nextProcedureTypes.length,
      surface,
    });
  };

  const getProcedureTypeImageUrl = (procedureType: ProcedureType) => {
    if (procedureType.image) {
      return uploadedImagePath(procedureType.image);
    }

    const fallbackProcedure = quoteData.procedures.find(
      (procedure: Procedure) =>
        procedure.isInternal !== true &&
        (procedure.procedureTypes || []).some(
          (pt) => pt.id === procedureType.id,
        ),
    );

    const procedureImage =
      quoteData.gender === "MALE"
        ? fallbackProcedure?.maleImage || fallbackProcedure?.image
        : fallbackProcedure?.image || fallbackProcedure?.maleImage;

    return procedureImage
      ? uploadedImagePath(procedureImage)
      : `${process.env.PUBLIC_URL}/img/app-logo.png`;
  };

  // procedureTypeOptions is already filtered to types with live procedures
  // (see above), so there is nothing left to drop here.
  const optionsRender = [
    <StyledRadioButton value="all" key="all">
      <span className="procedure-type-option">
        {t("procedures.showAll")}
        <UnorderedListOutlined />
      </span>
    </StyledRadioButton>,
    ...procedureTypeOptions.map((option: Option) => (
      <StyledRadioButton value={option.value} key={option.value}>
        <span className="procedure-type-option">{option.label}</span>
      </StyledRadioButton>
    )),
  ];

  return (
    <div className="select-procedureV2">
      {modalContextHolder}
      {storyProcedure && (
        <ErrorBoundary
          fallback={
            <ProcedureStoryUnavailable
              onClose={() => setStoryProcedure(null)}
            />
          }
        >
          <Suspense
            fallback={
              <ProcedureStoryLoading
                onClose={() => setStoryProcedure(null)}
              />
            }
          >
            <ProcedureStory
              procedure={storyProcedure}
              onClose={() => setStoryProcedure(null)}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      <Row>
        <Col span={isMobile ? 24 : 4}>
          {!isMobile && (
            <Radio.Group
              value={selectedProcedureType}
              onChange={(event: RadioChangeEvent) => {
                const nextType = event.target.value;
                selectExactMode(() => setSelectedProcedureType(nextType));
              }}
            >
              {optionsRender}
            </Radio.Group>
          )}
        </Col>
        <Col span={isMobile ? 24 : 12} className="select-procedureV2-main-col">
          <div className="select-procedureV2-main-header">
            {!isSimplifiedMode && (
              <QuoteFiltersForm
                procedureTypeOptions={procedureTypeOptions}
                onChangeProcedureTypeSelect={(value) => {
                  selectExactMode(() => setSelectedProcedureType(value));
                }}
                selectedProcedureType={selectedProcedureType}
              />
            )}
            {!isMobile && (
              <div className="select-procedures-title">
                {isSimplifiedMode
                  ? t("procedures.chooseAreaTitle", {
                      defaultValue: "Choose the areas you want help with",
                    })
                  : t("procedures.selectYourProcedures")}
              </div>
            )}
          </div>
          <div className="procedures-list-container" ref={proceduresListRef}>
            <div className="procedures-list-mobile-padding">
              {!isSimplifiedMode && (
                <button
                  className="help-choose-card"
                  type="button"
                  onClick={openSimplifiedMode}
                >
                  <span>
                    <strong>
                      {t("procedures.helpChooseTitle", {
                        defaultValue: "Not sure yet / Help me choose",
                      })}
                    </strong>
                    <small>
                      {t("procedures.helpChooseBody", {
                        defaultValue:
                          "Pick body areas instead of exact procedures. Our team can guide you from there.",
                      })}
                    </small>
                  </span>
                  <span className="help-choose-cta">
                    {t("procedures.helpChooseCta", {
                      defaultValue: "Choose areas",
                    })}
                  </span>
                </button>
              )}

              {isSimplifiedMode ? (
                <div className="simplified-procedure-type-panel">
                  <button
                    className="back-to-exact-procedures"
                    type="button"
                    onClick={() => selectExactMode()}
                  >
                    {t("procedures.backToExactProcedures", {
                      defaultValue: "Back to exact procedures",
                    })}
                  </button>
                  <div className="simplified-procedure-type-intro">
                    {t("procedures.chooseAreaBody", {
                      defaultValue:
                        "Select one or more areas. You can share details on the next steps.",
                    })}
                  </div>
                  {simplifiedProcedureTypes.length === 0 ? (
                    <div className="simplified-procedure-type-empty">
                      {t("procedures.noSimplifiedTypes", {
                        defaultValue:
                          "No procedure types are enabled for this flow yet.",
                      })}
                    </div>
                  ) : (
                    <div className="simplified-procedure-type-grid">
                      {simplifiedProcedureTypes.map((procedureType) => {
                        const isSelected = selectedProcedureTypeIds.includes(
                          procedureType.id,
                        );
                        const label = tProcedures(
                          `types.${procedureType.name}`,
                          {
                            defaultValue: procedureType.name,
                          },
                        );
                        const imageUrl = getProcedureTypeImageUrl(procedureType);

                        return (
                          <button
                            key={procedureType.id}
                            className={`simplified-procedure-type-card ${
                              isSelected ? "selected" : ""
                            }`}
                            type="button"
                            onClick={() => toggleProcedureType(procedureType)}
                          >
                            <img src={imageUrl} alt={label} loading="lazy" />
                            <span className="simplified-procedure-type-name">
                              {label}
                            </span>
                            <span className="simplified-procedure-type-select">
                              {isSelected && <CheckOutlined />}
                              {isSelected
                                ? t("procedures.areaSelected", {
                                    defaultValue: "Selected",
                                  })
                                : t("procedures.areaSelect", {
                                    defaultValue: "Select",
                                  })}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {selectedProcedureType !== "all" && (
                    <QuoteProceduresList
                      setStoryProcedure={setStoryProcedure}
                      procedureType={selectedProcedureType}
                    />
                  )}
                  {selectedProcedureType === "all" && (
                    <QuoteProceduresListAll
                      setStoryProcedure={setStoryProcedure}
                      procedureType={selectedProcedureType}
                    />
                  )}
                </>
              )}
              <div className="procedures-list-bottom-spacer" />
            </div>
          </div>
        </Col>
        <Col span={isMobile ? 24 : 8} className="select-procedureV2-side-col">
          {!isMobile && <QuoteSummary />}
          {!quoteData.selectedSurgeon && !isMobile && (
            <Card className="describe-detail-card">
              <Form layout="vertical">
                <Form.Item label={t("procedures.describeInDetail")}>
                  <TextArea
                    value={quoteData.procedureDescription}
                    onChange={(
                      event: React.ChangeEvent<HTMLTextAreaElement>,
                    ) => {
                      quoteData.setProcedureDescription(event.target.value);
                    }}
                  ></TextArea>
                </Form.Item>
              </Form>
            </Card>
          )}
          <div className="quote-button-mobile-container">
            {showDisabledHint && !hasSelection && (
              <div className="cta-disabled-hint" role="status">
                {t("procedures.ctaDisabledHint", {
                  defaultValue: "Select at least one procedure to continue",
                })}
              </div>
            )}
            <QuoteButton
              disabled={!hasSelection}
              onDisabledClick={handleDisabledCtaClick}
              onClick={() => {
                if (selectedSimplifiedCount > 0) {
                  trackQuoteSimplifiedProcedureCtaClicked(
                    selectedSimplifiedCount,
                    selectedProcedureTypeIds,
                  );
                } else {
                  trackQuoteProcedureCtaClicked(
                    selectedCount,
                    selectedProcedureIds,
                  );
                }
                next();
              }}
            >
              {ctaCopy}
            </QuoteButton>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Step1SelectProcedureV2;

export const StyledRadioButton = styled(Radio)`
  display: block;
  .ant-radio {
    display: none;
  }
  .ant-radio-wrapper span {
    display: block;
  }

  .procedure-type-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
  }
`;
